import { useState, useMemo, useEffect } from "react";
import { Box, Checkbox, FormControl, InputLabel, MenuItem, Select, TablePagination, Typography, Button } from "@mui/material";
import { useLazyQuery, gql } from "@apollo/client";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { GlobalModal } from "../../../features/modal/Modal";
import { handleModal } from "../../../features/modal/modalSlice";

import { useAppDispatch } from "../../../app/hooks";
import { IRegistrant } from "../../../types/registrant";
import { updateRegistrants } from "../../../features/projectSetting/projectSettingSlice";
import { camelToNormal, convertAllDates, normalToCamel, capitalizeFirstLetter, downloadExcel, downloadPdf } from "../../../utils/function";
import { useTemplatesQuery } from "../../../features/emailTemplate/emailTemplateHooks";
import { IMail } from "../../../types/mailSchedule";
import StandardTable from "../../tables/StandardTable";
import { useSelector } from "react-redux";
import { selectUser } from "../../../features/auth/authSlice";
import { showErrorSnackbar } from "../../../features/snackbar/snackbarSlice";
import { selectProject } from "../../../features/project/projectSlice";
import MassMail from "../../common/MassMail";
import { FlexBetween, FlexEnd } from "../../../commonStyles";

const MailingTable = () => {
  const user = useSelector(selectUser);
  const project = useSelector(selectProject);
  const { projectid, mailid } = useParams();
  const [getEmails] = useTemplatesQuery(1, 100, "_ID_DESC");
  const storeDispatch = useAppDispatch();
  let navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const [pageNumber, setPageNumber] = useState<number>(searchParams.get("page") ? parseInt(searchParams.get("page")!, 10) : 1);
  const [perPage, setPerPage] = useState<number>(searchParams.get("row") ? parseInt(searchParams.get("row")!, 10) : 15);
  const query = searchParams.get("type");
  const name = query ? query : "";
  const [registrants, setRegistrants] = useState<IMail[]>([]);
  const [selectedRegistrants, setSelectedRegistrants] = useState<string[]>([]);
  const [count, setCount] = useState<number>(0);
  const [modalType, setModalType] = useState<string>("");

  useEffect(() => {
    getEmails();
    // eslint-disable-next-line
  }, []);

  const [getRegistrants, { loading }] = useLazyQuery(GETREGISTRANTMAILS, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
    },
    onError: (e) => {
      console.log(e);
    },
  });

  const [getAllRegistrants] = useLazyQuery(GETALLREGISTRANTMAILS, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
    },
    onError: (e) => {
      console.log(e);
    },
  });

  useEffect(() => {
    getRegistrants({
      variables: {
        filter: {
          OR: normalToCamel(query!) === "unopened" ? [{ status: "sent" }, { status: "delivered" } ] : [{ status: normalToCamel(query!) }],
          mailSchedule: mailid,
        },
        page: pageNumber,
        perPage: perPage,
      },
    }).then((res) => {
      setCount(res.data.mailPagination.count);
      setRegistrants(res.data.mailPagination.items);
    });
  }, [pageNumber, projectid, mailid, query, getRegistrants, perPage]);

 const download = async (type: string, data: any) => {
    let allRegistrants = await getRegistrants({
      variables: {
        filter: {
          status: normalToCamel(query!) === "unopened" ? "sent" : normalToCamel(query!),
          mailSchedule: mailid,
        },
        page: 1,
        perPage: 40000,
      },
    });

    let mailColumns = [
      {
        label: "First Name",
        id: "firstName",
      },
      {
        label: "Last Name",
        id: "lastName",
      },
      {
        label: "Status",
        id: "status",
      },
      {
        label: "Last Clicked",
        id: "lastClicked",
      },
      {
        label: "Date Read",
        id: "dateRead",
      },
      {
        label: "Email",
        id: "email",
      },
      {
        label: "Phone Number",
        id: "phoneNumber",
      },
      {
        label: "Email Subscribed",
        id: "emailSubscribed",
      },
      {
        label: "Date Registered",
        id: "dateRegistered",
      },
    ];
    
    let widths: any = {
      firstName: 15,
      lastName: 15,
      status: 15,
      lastClicked: 15,
      dateRead: 15,
      email: 15,
      phoneNUmber: 15,
      emaiLSubscribed: 15,
      dateRegistered: 15
    };

    let pdfWidths: any = {
      firstName: 200,
      lastName: 200,
      status: 200,
      lastClicked: 200,
      dateRead: 200,
      email: 200,
      phoneNUmber: 200,
      emaiLSubscribed: 200,
      dateRegistered: 200,
    };
    
    let sheetTitle = "Mail Schedule";

    let allData = allRegistrants.data.mailPagination.items.map((data: any) => {
      return {
        firstName: data.registrant ? data.registrant.firstName : '',
        lastName: data.registrant ? data.registrant.lastName : '',
        status: data.status ? capitalizeFirstLetter(data.status) : "N/A",
        lastClicked: data.lastClicked ? data.lastClicked : 'N/A',
        dateRead: data.dateRead ? convertAllDates(new Date(data.dateRead), "PPpp") : 'N/A',
        email: data.email,
        phoneNumber: data.registrant ? data.registrant.primaryPhone : '',
        emailSubscribed: data.registrant ? data.registrant.emailUnsubscribed ? "No" : "Yes" : 'N/A',
        dateRegistered: data.registrant ? convertAllDates(data.registrant.createdAt, "PPpp") : "N/A"
      };
    });
    
    if (type === "excel") {
      downloadExcel([allData], [mailColumns], [], [[widths]], [sheetTitle], sheetTitle);
    } else {
      downloadPdf([allData], [mailColumns], [], [pdfWidths], [sheetTitle], sheetTitle);
    }
  };

  const columns = useMemo(() => {
    const selectAll = async () => {
      if (selectedRegistrants.length) {
        setSelectedRegistrants([]);
      } else {
        await getAllRegistrants({
          variables: {
            filter: {
              OR: normalToCamel(query!) === "unopened" ? [{ status: "sent" }, { status: "delivered" } ] : [{ status: normalToCamel(query!) }],
              mailSchedule: mailid,
            },
            page: 1,
            perPage: 100000,
          },
        }).then((res) => {
          let registrantIds = [...res.data.mailPagination.items].map((registrant: any) => registrant.registrant._id)
          setSelectedRegistrants(registrantIds);
        });
      }
    };

    const handleSelectedRegistrants = (id: string) => {
      if (selectedRegistrants.includes(id)) {
        setSelectedRegistrants(selectedRegistrants.filter((regId: string) => regId !== id));
      } else {
        setSelectedRegistrants([...selectedRegistrants, id]);
      }
    };

    const handleRatingChange = (e: string, name: string, data: IRegistrant) => {
      storeDispatch(
        updateRegistrants({
          value: e,
          name: name,
          data: data,
        })
      );
    };

    return [
      {
        id: "selection",
        Header: <Checkbox sx={{ color: "#fff" }} onClick={() => selectAll()} checked={selectedRegistrants.length > 0} />,
        accessor: (rowData: IMail) => {
          if (rowData.registrant) {
            return (
              <Box>
                <Checkbox
                  onChange={(e) => handleSelectedRegistrants(rowData.registrant._id)}
                  checked={selectedRegistrants.includes(rowData.registrant._id)}
                />
              </Box>
            );
          }
        },
      },
      {
        Header: "First Name",
        accessor: (rowData: IMail) => {
          if (rowData.registrant) {
            return (
              <strong
                style={{ cursor: "pointer" }}
                onClick={() => navigate(`/dashboard/project/${rowData.project._id}/registrant/${rowData.registrant._id}`)}
              >
                {rowData.registrant.firstName}
              </strong>
            );
          } else return "";
        },
      },
      {
        Header: "Last Name",
        accessor: (rowData: IMail) => {
          if (rowData.registrant) {
            return (
              <strong
                style={{ cursor: "pointer" }}
                onClick={() => navigate(`/dashboard/project/${rowData.project._id}/registrant/${rowData.registrant._id}`)}
              >
                {rowData.registrant.lastName}
              </strong>
            );
          } else return "";
        },
      },
      {
        Header: "Status",
        accessor: (rowData: IMail) => {
          return rowData.status ? capitalizeFirstLetter(rowData.status) : "N/A";
        },
      },
      {
        Header: "Last Clicked",
        accessor: (rowData: IMail) => {
          return <Box sx={{ width: '400px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap'}}>{rowData.lastClicked ? rowData.lastClicked : "N/A"}</Box>
        },
      },
      {
        Header: "Date Read",
        accessor: (rowData: IMail) => {
          return rowData.dateRead ? convertAllDates(new Date(rowData.dateRead), "PP") : "N/A";
        },
      },
      {
        Header: "Email",
        accessor: (rowData: IMail) => {
          return rowData.email;
        },
      },
      {
        Header: "Phone Number",
        accessor: (rowData: IMail) => {
          return rowData.registrant ? rowData.registrant.primaryPhone : "";
        },
      },
      {
        Header: "Rating",
        accessor: (rowData: IMail) => {
          if (rowData.registrant) {
            return (
              <FormControl fullWidth>
                <InputLabel id="demo-simple-select-label">Rating</InputLabel>
                <Select
                  labelId="ratings"
                  name="rating"
                  value={rowData.registrant.rating}
                  label="Rating"
                  onChange={(e) => handleRatingChange(e.target.value, e.target.name, rowData.registrant)}
                >
                  <MenuItem value={"N"}>N</MenuItem>
                  <MenuItem value={"A"}>A</MenuItem>
                  <MenuItem value={"B"}>B</MenuItem>
                  <MenuItem value={"C"}>C</MenuItem>
                  <MenuItem value={"P"}>P</MenuItem>
                  <MenuItem value={"R"}>R</MenuItem>
                  <MenuItem value={"NQ"}>NQ</MenuItem>
                  <MenuItem value={"Spam"}>Spam</MenuItem>
                </Select>
              </FormControl>
            );
          } else return "";
        },
      },
      {
        Header: "Source",
        accessor: (rowData: IMail) => {
          return rowData.registrant ? camelToNormal(rowData.registrant.source) : "";
        },
      },
      {
        Header: "Realtor Type",
        accessor: (rowData: IMail) => {
          return rowData.registrant ? camelToNormal(rowData.registrant.realtorType) : "";
        },
      },
      {
        Header: "Email Subscribed",
        accessor: (rowData: IMail) => {
          return rowData.registrant ? (rowData.registrant.emailUnsubscribed ? "No" : "Yes") : "";
        },
      },
      {
        Header: "Date Registered",
        accessor: (rowData: IMail) => {
          return rowData.registrant ? convertAllDates(rowData.registrant.createdAt, "PPpp") : "";
        },
      },
    ];
  }, [selectedRegistrants, mailid, storeDispatch, navigate, projectid, query]);

  // Functions

  const handleGlobalFilterValue = (value: string) => {
    setPageNumber(1);
    getRegistrants({
      variables: {
        filter: {
          status: normalToCamel(query!) === "unopened" ? "sent" : normalToCamel(query!),
          mailSchedule: mailid,
          search: value,
        },
        page: 1,
        perPage: 15,
      },
    });
  };

  const handleQueryParams = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setSearchParams({ page: (newPage + 1).toString(), row: perPage.toString(), type: query! });
    setPageNumber(newPage + 1);
  };

  const handlePerRows = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPerPage(parseInt(event.target.value, 10));
    setSearchParams({ row: event.target.value, page: "1" });
    setPageNumber(1);
  };

  const handleModalView = (type: string) => {
    if (type === "mail" || type === "text" || type === "reassign" || type === "terminate" || type === "tags") {
      if (!selectedRegistrants.length) return storeDispatch(showErrorSnackbar("No Registrants Selected"));
    }
    setModalType(type);
    storeDispatch(handleModal(true));
  };

  return (
    <div>
      <GlobalModal>
        {modalType === "mail" ? <MassMail id={"registrant"} selectedRegistrants={selectedRegistrants} project={project!} /> : null}
      </GlobalModal>
      <FlexBetween sx={{ mb: 2 }}>
        <Box>
          {query ? 
          <Typography variant="h5" sx={{ mb: 2 }}>
            <strong>{query}</strong>
          </Typography>
          : null}
        </Box>
        <Box>
          {user?.type !== "Developer" && selectedRegistrants.length ? (
            <Button sx={{ mr: 1 }} onClick={() => handleModalView("mail")} color="primary" variant="contained">
              Mass Mail
            </Button>
          ) : null}
        </Box>
      </FlexBetween>
      <StandardTable
        data={registrants}
        loading={loading}
        columns={columns}
        count={count}
        download={download}
        user={user}
        handleGlobalFilterValue={handleGlobalFilterValue}
        title={query ? query : ""}
      />
      <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
        <TablePagination
          component="div"
          count={count}
          page={pageNumber - 1}
          onPageChange={handleQueryParams}
          rowsPerPage={perPage!}
          rowsPerPageOptions={[15, 25, 50, 100]}
          onRowsPerPageChange={handlePerRows}
        />
      </Box>
    </div>
  );
};

const GETREGISTRANTMAILS = gql`
  query mailPagination($filter: FilterFindManyMailInput, $page: Int, $perPage: Int) {
    mailPagination(filter: $filter, page: $page, perPage: $perPage) {
      items {
        lastClicked
        status
        email
        dateRead
        registrant {
          _id
          firstName
          lastName
          email
          primaryPhone
          rating
          source
          realtorType
          emailUnsubscribed {
            type
            date
          }
          createdAt
        }
      }
      count
    }
  }
`;

const GETALLREGISTRANTMAILS = gql`
  query mailPagination($filter: FilterFindManyMailInput, $page: Int, $perPage: Int) {
    mailPagination(filter: $filter, page: $page, perPage: $perPage) {
      items {
        registrant {
          _id
        }
      }
    }
  }
`;

export default MailingTable;
